import { ListChecks, FileText, Gamepad2, PenTool, type LucideIcon } from 'lucide-react';

/**
 * Practice item kinds — MVP registry.
 *
 * Built-in only. Each kind delegates to existing flows (no rewrites):
 *   - question_set   → /test/...               (instructor_tests)
 *   - worksheet      → /worksheets/...
 *   - game           → /games/...
 *   - writing_prompt → /writing/...            (grade-only for now)
 *
 * Adding a kind later = code change + migration to seed
 * `org_practice_settings` rows. No `practice_item_kinds` table.
 */
export type PracticeKindId =
  | 'question_set'
  | 'worksheet'
  | 'game'
  | 'writing_prompt';

export type PracticeCategory = 'assessment' | 'practice' | 'production';

export interface PracticeKind {
  id: PracticeKindId;
  label: string;
  description: string;
  icon: LucideIcon;
  category: PracticeCategory;
  /** Column on lesson_practice_items that holds the FK (null = grade-only). */
  refColumn: 'test_id' | 'worksheet_id' | 'game_id' | null;
}

export const PRACTICE_KINDS: Record<PracticeKindId, PracticeKind> = {
  question_set: {
    id: 'question_set',
    label: 'Test',
    description: 'A graded question set built in the instructor test editor.',
    icon: ListChecks,
    category: 'assessment',
    refColumn: 'test_id',
  },
  worksheet: {
    id: 'worksheet',
    label: 'Worksheet',
    description: 'A printable or interactive worksheet.',
    icon: FileText,
    category: 'practice',
    refColumn: 'worksheet_id',
  },
  game: {
    id: 'game',
    label: 'Game',
    description: 'An interactive learning game.',
    icon: Gamepad2,
    category: 'practice',
    refColumn: 'game_id',
  },
  writing_prompt: {
    id: 'writing_prompt',
    label: 'Writing prompt',
    description: 'Open the writing pad for the lesson grade.',
    icon: PenTool,
    category: 'production',
    refColumn: null,
  },
};

export const PRACTICE_KIND_LIST: PracticeKind[] = Object.values(PRACTICE_KINDS);

export function isPracticeKindId(value: string): value is PracticeKindId {
  return value in PRACTICE_KINDS;
}
